import React, { memo } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import './index.less';

export interface QdLoadingProps {
  /**
   * 是否显示 loading
   */
  loading?: boolean;
  /**
   * loading 提示文字
   */
  tip?: string;
  /**
   * 自定义图标大小
   */
  size?: number;
  /**
   * 遮罩层透明度 0-1
   */
  opacity?: number;
  /**
   * 遮罩层背景色
   */
  maskColor?: string;
  /**
   * 图标颜色
   */
  iconColor?: string;
  /**
   * 是否使用绝对定位（默认 true，适合大多数场景）
   * 如果父元素没有设置 position，请确保父元素设置了 position: relative
   */
  absolute?: boolean;
  /**
   * z-index 值
   */
  zIndex?: number;
}

/**
 * QdLoading - 高性能 Loading 组件
 *
 * 性能优化特点：
 * 1. 使用 React.memo 避免不必要的重渲染
 * 2. 使用 CSS transform 做动画，触发 GPU 加速
 * 3. 使用 will-change 提示浏览器优化
 * 4. loading 为 false 时不渲染 DOM，减少 DOM 节点
 * 5. 使用 pointer-events 阻止交互，避免 JS 事件处理
 * 6. 简化 DOM 结构，减少渲染开销
 *
 * @example
 * ```tsx
 * // 基础使用
 * <div style={{ position: 'relative', height: 400 }}>
 *   <YourContent />
 *   <QdLoading loading={isLoading} />
 * </div>
 *
 * // 带提示文字
 * <QdLoading loading={true} tip="加载中..." />
 *
 * // 自定义样式
 * <QdLoading
 *   loading={true}
 *   size={32}
 *   iconColor="#5f49e5"
 *   opacity={0.7}
 * />
 * ```
 */
const QdLoading: React.FC<QdLoadingProps> = memo(({
  loading = false,
  tip,
  size = 24,
  opacity = 0.7,
  maskColor = '#fff',
  iconColor = '#5f49e5',
  absolute = true,
  zIndex = 1000,
}) => {
  // loading 为 false 时不渲染，减少 DOM 节点
  if (!loading) {
    return null;
  }

  return (
    <div
      className={`qd-loading-mask ${absolute ? 'qd-loading-mask-absolute' : 'qd-loading-mask-fixed'}`}
      style={{
        backgroundColor: maskColor,
        opacity,
        zIndex,
      }}
    >
      <div className="qd-loading-content">
        <LoadingOutlined
          style={{
            fontSize: size,
            color: iconColor,
          }}
          spin
        />
        {tip && <div className="qd-loading-tip">{tip}</div>}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // 自定义比较函数，只在关键属性变化时重渲染
  return (
    prevProps.loading === nextProps.loading &&
    prevProps.tip === nextProps.tip &&
    prevProps.size === nextProps.size &&
    prevProps.opacity === nextProps.opacity &&
    prevProps.maskColor === nextProps.maskColor &&
    prevProps.iconColor === nextProps.iconColor &&
    prevProps.absolute === nextProps.absolute &&
    prevProps.zIndex === nextProps.zIndex
  );
});

QdLoading.displayName = 'QdLoading';

export default QdLoading;

